import { FieldValues } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { TICKET_LIST_ACTION_TYPES } from '@constants/actionTypes/ticketListActionTypes';
import { API_STATUS_CODES } from '@constants/common.constants';
import { ROOT_STATE } from '@store/store.types';
import { AvDecision, taskStatusTypes, ticketStatusTypes } from '../constants/ticketlist.constants';
import { buildCompleteTaskPayload, refreshTicketList, updateTicketList } from '../constants/ticketlist.utils';
import { useApiHandler } from './useApiHandler';


export const useAutoVerification = () => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const { completeUserTaskAPI, defaultListAPI, listAPI, filteredListAPI } = useApiHandler();

    const {
        tickets: { ticketListJson },
        modalData: { activitiTaskId, processInstanceId, ticketIdVerify, taskId },
        modalFlags: { validateVerifyComments },
        autoVerification,
        listApiType,
    } = useSelector((state: ROOT_STATE) => state.ticketMgmt.ticketsList);

    const submitManualReview = async (formData: FieldValues, onClose: () => void) => {
        const { decision, rejectReason, internalComment, externalComment, improvementFeedback } = formData;

        const payload = buildCompleteTaskPayload({
            activitiTaskId,
            processInstanceId,
            taskId,
            ticketId: ticketIdVerify,
            validateComments: validateVerifyComments,
            taskStatus: decision === AvDecision.REJECTED ? AvDecision.REJECTED : AvDecision.ACCEPTED,
            taskRejectionReason: decision === AvDecision.REJECTED ? rejectReason : '',
            internalComment,
            externalComment,
            extraFields: {
                csrDecision: decision,
                avResult: autoVerification?.result,
                avImprovementFeedback: improvementFeedback,
            },
        });

        const response = await completeUserTaskAPI(dispatch, payload);
        if (response?.statusCode === API_STATUS_CODES.OK) {
            updateTicketList(dispatch, ticketListJson, 'encryptedId', ticketIdVerify, {
                taskStatus: taskStatusTypes.RESOLVED,
                status: ticketStatusTypes.RESOLVED,
            });
            refreshTicketList({ dispatch, listApiType, defaultListAPI, listAPI, filteredListAPI });
            onClose();
            return;
        }

        // completeUserTaskAPI already dispatches SET_DATA_ERROR on thrown failures and returns
        // undefined — avoid a second overwrite. Only handle non-OK resolved responses here.
        if (response == null) {
            return;
        }

        dispatch({
            type: TICKET_LIST_ACTION_TYPES.SET_DATA_ERROR,
            payload: {
                isOpen: true,
                errorMessage: intl.formatMessage({ id: 'ticket.autoVerification.msg.completeFailed' }),
                modalTitle: 'ticketList.modal.errorModal.header.error',
            },
        });
    };

    return { autoVerification, submitManualReview };
};
